import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type { Mutation, ShipmentTypeFilter } from 'graphql-models';
import unlockBoxMutation from 'walden-trace-app/graphql/shipments/unlock-box.graphql';
import type { AppShipmentsModel } from 'walden-trace-app/routes/app/shipments';
import type ApiService from 'walden-trace-app/services/api';

export default class AppShipmentsIndexController extends Controller {
  declare model: AppShipmentsModel;

  @tracked unlockBoxError = false;
  @tracked search = '';
  @tracked shipmentType: ShipmentTypeFilter | undefined;
  @tracked shipmentState: 'outForDelivery' | 'planned' | 'delivered' | 'inTransit' | undefined;
  @tracked showAll: 'outForDelivery' | 'planned' | 'delivered' | 'inTransit' | undefined;
  @tracked fromDate = '';
  @tracked toDate = '';
  @tracked isLoading = false;
  @tracked inputFocused = false;

  queryParams = ['search', 'shipmentType', 'fromDate', 'toDate', 'shipmentState', 'showAll'];

  @service('api') declare apiService: ApiService;
  @service('router') declare routerService: RouterService;
  @service('intl') declare intlService: IntlService;

  // todo: could / should we extract this somehow, because it is the same method as used on the detail screen
  unlockBox = task(async (boxId: string) => {
    try {
      // TODO: this should be a confirmation dialog, not a window alert
      window.alert(this.intlService.t('pages.shipments.details.unlockBox.description'));
      this.unlockBoxError = false;

      const result = await this.apiService.mutate<Mutation['unlockBox']>({
        mutation: unlockBoxMutation,
        variables: {
          id: boxId,
        },
      });

      this.routerService.refresh();

      return result;
    } catch (error) {
      console.error(error);
      this.unlockBoxError = true;
      return error;
    }
  });

  get activeFilters() {
    return (
      (this.shipmentType === ('BOX' as ShipmentTypeFilter) ? 1 : 0) +
      (this.fromDate?.length ? 1 : 0) +
      (this.toDate?.length ? 1 : 0)
    );
  }

  get shipmentStateOptions() {
    return [
      {
        count: this.model.delivered.paginatorInfo.total,
        iconName: 'check-circle-fill',
        label: this.intlService.t('pages.shipments.filters.delivered'),
        value: 'delivered',
      },
      {
        count: this.model.outForDelivery.paginatorInfo.total,
        iconName: 'truck',
        label: this.intlService.t('pages.shipments.filters.outForDelivery'),
        value: 'outForDelivery',
      },
      {
        count: this.model.inTransit.paginatorInfo.total,
        iconName: 'arrow-right-left',
        label: this.intlService.t('pages.shipments.filters.inTransit'),
        value: 'inTransit',
      },
      {
        count: this.model.planned.paginatorInfo.total,
        iconName: 'package-check',
        label: this.intlService.t('pages.shipments.filters.planned'),
        value: 'planned',
      },
    ];
  }

  @action
  updateShipmentState(state: string) {
    if (state === this.shipmentState) {
      this.shipmentState = undefined;
    } else {
      this.shipmentState = state as 'outForDelivery' | 'planned' | 'delivered' | 'inTransit';
    }
  }

  @action
  clearSearch() {
    this.search = '';
    this.inputFocused = false;
  }
}
